import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trustService } from '../../services/api';
import { Shield, DollarSign, TrendingUp, AlertTriangle, ArrowRight, Users } from 'lucide-react';

const TrustSummaryWidget = () => {
  const [trustSummary, setTrustSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTrustSummary();
  }, []);

  const fetchTrustSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trustService.getTrustSummary();
      setTrustSummary(response.data);
    } catch (error) {
      console.error('Error fetching trust summary:', error);
      setError('Failed to load trust summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 w-32 bg-gray-200 rounded"></div>
            <div className="h-4 w-4 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-3">
            <div className="h-8 w-full bg-gray-200 rounded"></div>
            <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
            <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Shield className="mr-2 h-5 w-5 text-yellow-600" />
            Trust Summary
          </h3>
          <button 
            onClick={fetchTrustSummary}
            className="text-yellow-600 hover:text-gray-800 text-sm"
          >
            Retry
          </button>
        </div>
        <div className="text-center py-4">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Shield className="mr-2 h-5 w-5 text-yellow-600" />
            Trust Summary
          </h3>
          <Link 
            to="/trust-accounts" 
            className="text-yellow-600 hover:text-gray-800 flex items-center text-sm font-medium"
          >
            View All
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-6">
        <div className="space-y-4">
          {/* Total Trust Balance */}
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-900">Total Trust Balance</p>
                <p className="text-lg font-bold text-green-900">
                  {trustService.formatCurrency(trustSummary?.total_trust_balance || 0)}
                </p>
              </div>
            </div>
            <div className="text-right text-sm text-green-700">
              {trustSummary?.active_accounts_count || 0} accounts
            </div>
          </div>

          {/* Available Balance */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Available Balance</p>
                <p className="text-lg font-bold text-gray-900">
                  {trustService.formatCurrency(trustSummary?.total_available_balance || 0)}
                </p>
              </div>
            </div>
            <div className="text-sm text-yellow-600">
              {((trustSummary?.total_available_balance || 0) / (trustSummary?.total_trust_balance || 1) * 100).toFixed(0)}% available
            </div>
          </div>

          {/* Reserved Balance */}
          <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Shield className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-yellow-900">Reserved Funds</p>
                <p className="text-lg font-bold text-yellow-900">
                  {trustService.formatCurrency(trustSummary?.total_reserved_balance || 0)}
                </p>
              </div>
            </div>
            <div className="text-sm text-yellow-700">
              For pending projects
            </div>
          </div>

          {/* Accounts Needing Attention */}
          {trustSummary?.accounts_requiring_attention > 0 && (
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-900">Needs Attention</p>
                  <p className="text-lg font-bold text-red-900">
                    {trustSummary.accounts_requiring_attention} accounts
                  </p>
                </div>
              </div>
              <Link 
                to="/trust-accounts?filter=low-balance" 
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Review →
              </Link>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h4>
          <div className="grid grid-cols-2 gap-3">
            <Link 
              to="/trust-accounts/new"
              className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            >
              <Users className="h-4 w-4 text-yellow-600 mr-2" />
              <span className="text-gray-900">New Account</span>
            </Link>
            <button className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm">
              <DollarSign className="h-4 w-4 text-green-600 mr-2" />
              <span className="text-gray-900">Add Deposit</span>
            </button>
          </div>
        </div>

        {/* Last Updated */}
        <div className="mt-4 text-xs text-gray-500 text-center">
          Last updated: {trustService.formatDateTime(trustSummary?.last_updated || new Date())}
        </div>
      </div>
    </div>
  );
};

export default TrustSummaryWidget;